/**
 * The AudioEngine implementation: master chain, drone runtimes (FX bus + prepared sources), voices, the
 * continuous voice per drone, the implied tanpura, voice stealing, the 30 Hz tick (modulation + store meters)
 * and recovery when the ContextManager rebuilds the AudioContext.
 */
import { glideSeconds, hzToMidi, parseDrone, type Drone, midiToHz } from '@tonist/shared';
import { engineStore, type EngineVoiceMeter, type LoadProgress, type LoadStatus } from '@/store/engineStore';
import { ContextManager } from './context';
import { DroneGraph } from './droneGraph';
import { audioLog, fmtHz, setLogClock, audioDebugEnabled } from './log';
import { ticker } from './modulation';
import { clamp, isTier0, macroMap, pitchClassDistance, targetTau, type MacroMap } from './params';
import { Registry, type PreparedSample } from './registry';
import { blockMsFor, mapLimit, mergeRoots, renderRoot, rootLoopCap, selectRoots, type RootBuffer } from './sampler';
import { Tanpura } from './tanpura';
import type { AudioEngine, EngineState, GestureState, PitchEngineMode, VoiceHandle } from './types';
import { Voice, type PreparedSource } from './voice';

export const MAX_VOICES = 6;
const TICK_MS = 33;
const IDLE_MS = 20000;

interface DroneRuntime {
  drone: Drone;
  macros: MacroMap;
  graph: DroneGraph;
  sources: PreparedSource[];
  continuous: Voice | null;
  status: LoadStatus;
  /** phase 1: base buffers decoded (the drone plays) */
  loadPromise: Promise<void> | null;
  /** phase 2: multisample roots rendered in the background */
  rootsPromise: Promise<void> | null;
  sourcesKey: string;
  gen: number;
}

interface Master { input: GainNode; comp: DynamicsCompressorNode; limiter: DynamicsCompressorNode; gain: GainNode; analyser: AnalyserNode; ctx: AudioContext }

function channelsOf(buffer: AudioBuffer): Float32Array[] {
  const out: Float32Array[] = [];
  for (let c = 0; c < buffer.numberOfChannels; c++) out.push(buffer.getChannelData(c));
  return out;
}

export class Engine implements AudioEngine {
  readonly samples: Registry;
  private readonly cm: ContextManager;
  private master: Master | null = null;
  private readonly drones = new Map<string, DroneRuntime>();
  private voiceList: Voice[] = [];
  private nextVoiceId = 1;
  private tanpura: Tanpura | null = null;
  private tonicHz = 0;
  private masterVolume = 0.8;
  private timer: ReturnType<typeof setInterval> | null = null;
  private lastTick = 0;
  private lastGestureAt = 0;
  private readonly spectrum = new Uint8Array(64);
  private readonly lowPower = isTier0();
  /** set when a render turns out slower than real time: the device is slow whatever it claims about itself */
  private slowRender = false;
  private get tier0(): boolean { return this.lowPower || this.slowRender; }
  private readonly rootCache = new Map<string, Promise<RootBuffer>>();
  private readonly pendingLoads = new Map<string, { drone: Drone; resolve: () => void; reject: (e: unknown) => void }>();
  private lastVoiceCount = -1;
  private pitchMode: PitchEngineMode = 'multisample';
  private preview: { src: AudioBufferSourceNode; gain: GainNode; id: string } | null = null;
  /** at most two phase-vocoder renders in flight across all drones (each is its own OfflineAudioContext + worklet) */
  private renderSlots = 0;
  private readonly renderWaiters: Array<() => void> = [];

  constructor() {
    this.cm = new ContextManager({
      onState: (s: EngineState) => engineStore.setState({ state: s }),
      onRebuild: (ctx, reason) => this.rebuild(ctx, reason),
    });
    this.samples = new Registry(() => this.cm.ctx);
  }

  get state(): EngineState { return this.cm.state; }
  get tier(): 0 | 1 { return this.tier0 ? 0 : 1; }

  async start(): Promise<void> {
    await this.cm.start();
    this.ensureMaster();
    this.ensureTanpura();
    engineStore.setState({ sampleRate: this.cm.sampleRate });
    void this.samples.refresh().catch(() => { /* logged inside */ });
    this.startTimer();
    // phase 1 of every pending load (decode only) completes before start() resolves, so a noteOn right after
    // `await engine.start()` already has real sources
    const pending = Array.from(this.pendingLoads.values());
    this.pendingLoads.clear();
    await Promise.all(pending.map((p) => this.loadDrone(p.drone).then(p.resolve, p.reject)));
  }

  // ---- graph plumbing -------------------------------------------------------------------------------------------

  private ensureMaster(): Master {
    const ctx = this.cm.current;
    if (this.master && this.master.ctx === ctx) return this.master;
    const input = ctx.createGain();
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -6;
    comp.ratio.value = 4;
    comp.knee.value = 6;
    comp.attack.value = 0.005;
    comp.release.value = 0.25;
    // safety limiter after the musical compressor: nothing reaches the output above −1 dBFS, however many voices
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -1;
    limiter.knee.value = 0;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.001;
    limiter.release.value = 0.05;
    const gain = ctx.createGain();
    gain.gain.value = this.masterVolume;
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 128;
    analyser.smoothingTimeConstant = 0.7;
    input.connect(comp);
    comp.connect(limiter);
    limiter.connect(gain);
    gain.connect(analyser);
    analyser.connect(ctx.destination);
    this.master = { input, comp, limiter, gain, analyser, ctx };
    setLogClock(() => ctx.currentTime);
    audioLog.log('context', `master chain built (sampleRate ${ctx.sampleRate}, compressor −6 dB/4:1 → limiter −1 dB/20:1)`);
    const nav = navigator as Navigator & { deviceMemory?: number };
    audioLog.log('context', `device: ${nav.hardwareConcurrency ?? '?'} cores, ${nav.deviceMemory ?? 'unreported'} GB → pitch by ${this.tier0 ? 'resampling (low power)' : 'multisample rendering'}`);
    return this.master;
  }

  private ensureTanpura(): Tanpura {
    const m = this.ensureMaster();
    if (!this.tanpura) {
      this.tanpura = new Tanpura(m.ctx, m.input);
      if (this.tonicHz > 0) this.tanpura.setHz(this.tonicHz / 2, m.ctx.currentTime);
    }
    return this.tanpura;
  }

  private async rebuild(ctx: AudioContext, reason: string): Promise<void> {
    for (const v of this.voiceList) v.dispose();
    this.voiceList = [];
    this.samples.invalidate();
    this.rootCache.clear();
    this.master = null;
    this.tanpura?.dispose();
    this.tanpura = null;
    const m = this.ensureMaster();
    this.ensureTanpura();
    for (const rt of this.drones.values()) {
      rt.graph.dispose();
      rt.graph = new DroneGraph(ctx, rt.drone, rt.macros, this.cm.worklets);
      rt.graph.output.connect(m.input);
      rt.sources = [];
      rt.continuous = null;
      rt.loadPromise = null;
      rt.rootsPromise = null;
      rt.sourcesKey = '';
      void this.loadDrone(rt.drone).catch(() => { /* reported in the store */ });
    }
    engineStore.setState({ sampleRate: ctx.sampleRate, voices: [] });
    console.warn(`[tonist] engine rebuilt (${reason})`);
  }

  private setLoading(id: string, status: LoadStatus, message?: string): void {
    const s = engineStore.getState();
    const loading = { ...s.loading, [id]: status };
    const loadErrors = { ...s.loadErrors };
    if (status === 'error' && message) loadErrors[id] = message; else delete loadErrors[id];
    const loadedDrones = Array.from(this.drones.keys());
    engineStore.setState({ loading, loadErrors, loadedDrones });
  }

  private runtimeFor(drone: Drone): DroneRuntime {
    let rt = this.drones.get(drone.id);
    if (rt) return rt;
    const m = this.ensureMaster();
    const macros = macroMap(drone.macros);
    const graph = new DroneGraph(m.ctx, drone, macros, this.cm.worklets);
    graph.output.connect(m.input);
    rt = { drone, macros, graph, sources: [], continuous: null, status: 'loading', loadPromise: null, rootsPromise: null, sourcesKey: '', gen: 0 };
    this.drones.set(drone.id, rt);
    return rt;
  }

  /** Runtime by id; an unknown id gets a placeholder additive drone so the instrument still sounds. */
  private runtimeById(droneId: string): DroneRuntime {
    const rt = this.drones.get(droneId);
    if (rt) return rt;
    const placeholder = parseDrone({ id: droneId, name: droneId, sources: [{ kind: 'synth', engine: 'additive' }] });
    const made = this.runtimeFor(placeholder);
    made.status = 'error';
    this.setLoading(droneId, 'error', 'drone was not loaded; playing a placeholder synth');
    return made;
  }

  // ---- drones ---------------------------------------------------------------------------------------------------

  loadDrone(drone: Drone): Promise<void> {
    if (!this.cm.ctx) {
      // no context yet: remember the request and prefetch the bytes so decoding is immediate after start()
      for (const s of drone.sources) if (s.kind !== 'synth') void this.samples.prefetch(s.sampleId).catch(() => { /* reported at load */ });
      return new Promise<void>((resolve, reject) => { this.pendingLoads.set(drone.id, { drone, resolve, reject }); });
    }
    const rt = this.runtimeFor(drone);
    const key = JSON.stringify(drone.sources);
    const t = this.cm.current.currentTime;
    rt.drone = drone;
    rt.macros = macroMap(drone.macros);
    rt.graph.apply(drone, rt.macros, t);
    if (rt.loadPromise && rt.sourcesKey === key) return rt.loadPromise;
    rt.sourcesKey = key;
    const gen = ++rt.gen;
    rt.status = 'loading';
    this.setLoading(drone.id, 'loading');
    const t0 = performance.now();
    this.setProgress(drone.id, 'fetching', 'fetching samples', 0.05);
    audioLog.log('load', () => `${drone.id}: loading sources [${drone.sources.map((l) => l.kind === 'synth' ? `synth:${l.engine}` : `${l.kind}:${l.sampleId}`).join(', ')}]`);
    rt.rootsPromise = null;
    rt.loadPromise = this.prepareSources(drone).then(({ sources, error, upgrades }) => {
      if (rt.gen !== gen || this.drones.get(drone.id) !== rt) return;
      rt.sources = sources;
      rt.status = error ? 'error' : 'ready';
      this.setLoading(drone.id, rt.status, error);
      if (error) this.setProgress(drone.id, 'error', error, 1);
      else if (!upgrades.length) this.setProgress(drone.id, 'ready', 'ready', 1);
      else this.setProgress(drone.id, 'rendering', 'ready to play; rendering extra roots', 0.55);
      audioLog.log('load', () => `${drone.id}: phase 1 ${rt.status} in ${(performance.now() - t0).toFixed(0)}ms sources=[${sources.map((x) => x.kind + (x.kind === 'sample' ? `(${x.roots.filter((r) => r.real).length} real root${x.roots.filter((r) => r.real).length === 1 ? '' : 's'}: ${x.roots.filter((r) => r.real).map((r) => r.midi.toFixed(0)).join('/')})` : '')).join(', ')}]${error ? ` error: ${error}` : ''}${upgrades.length ? `; rendering ${upgrades.length} layer(s) of extra roots in the background` : ''}`);
      // voices struck while decoding are playing stand-in synths: swap in the real units now
      for (const v of this.voiceList) if (v.droneId === drone.id && v.usesFallback && !v.done && !v.releasing) v.upgradeSources(sources);
      if (upgrades.length) {
        this.setRendering(drone.id, true);
        rt.rootsPromise = Promise.all(upgrades.map((u) => u())).then(() => {
          if (rt.gen === gen) { this.setRendering(drone.id, false); this.setProgress(drone.id, 'ready', 'ready', 1); }
          audioLog.log('load', () => `${drone.id}: phase 2 done in ${(performance.now() - t0).toFixed(0)}ms, roots now [${rt.sources.filter((x) => x.kind === 'sample').map((x) => x.kind === 'sample' ? x.roots.map((r) => r.midi.toFixed(0) + (r.real ? '' : 'r')).join('/') : '').join(' | ')}] (MIDI, r = rendered)`);
        });
      }
    });
    return rt.loadPromise;
  }

  /** Resolves when the drone's multisample roots have finished rendering (phase 2), or immediately. */
  rootsReady(droneId: string): Promise<void> {
    return this.drones.get(droneId)?.rootsPromise ?? Promise.resolve();
  }

  private setProgress(id: string, phase: LoadProgress['phase'], label: string, fraction: number): void {
    const progress = { ...engineStore.getState().progress, [id]: { phase, label, fraction: clamp(fraction, 0, 1), updatedAt: performance.now() } };
    engineStore.setState({ progress });
    if (phase === 'ready' || phase === 'error') {
      setTimeout(() => {
        const cur = engineStore.getState().progress;
        if (cur[id]?.phase === phase) { const next = { ...cur }; delete next[id]; engineStore.setState({ progress: next }); }
      }, 1800);
    }
  }

  private setRendering(id: string, on: boolean): void {
    const rendering = { ...engineStore.getState().rendering };
    if (on) rendering[id] = true; else delete rendering[id];
    engineStore.setState({ rendering });
  }

  /**
   * Phase 1 (awaited): decode base buffers so every layer can sound with a single root.
   * Phase 2 (returned as `upgrades`, run in the background): render the extra multisample roots and merge them
   * into the layer's shared `roots` array in place; voices pick from the upgraded set at their next root switch.
   */
  private async prepareSources(drone: Drone): Promise<{ sources: PreparedSource[]; error?: string; upgrades: Array<() => Promise<void>> }> {
    let error: string | undefined;
    const upgrades: Array<() => Promise<void>> = [];
    const sources = await Promise.all(drone.sources.map(async (layer): Promise<PreparedSource> => {
      try {
        if (layer.kind === 'synth') return { kind: 'synth', layer };
        const ps = await this.samples.preparedSample(layer.sampleId);
        const sr = ps.buffer.sampleRate;
        if (layer.kind === 'freeze') {
          return {
            kind: 'freeze', layer,
            sample: { buffer: ps.buffer, channels: channelsOf(ps.buffer), sampleRate: sr, loopStart: Math.floor(ps.loopStart * sr), loopEnd: Math.floor(ps.loopEnd * sr), rootHz: ps.rootHz },
          };
        }
        // the built sample's root is the truth: presets (and drones cloned from them) carry a copy of rootHz that goes
        // stale when a sample is rebuilt at another pitch, which used to detune every note of the drone silently
        const rootHz = ps.rootHz > 0 ? ps.rootHz : (layer.rootHz ?? midiToHz(layer.rootMidi));
        const rootMidi = hzToMidi(rootHz);
        const base: RootBuffer = { midi: rootMidi, buffer: ps.buffer, loopStart: ps.loopStart, loopEnd: ps.loopEnd };
        const mode = this.pitchMode;
        if (mode === 'granular') {
          return { kind: 'granular', layer, sample: { buffer: ps.buffer, channels: channelsOf(ps.buffer), sampleRate: sr, loopStart: Math.floor(ps.loopStart * sr), loopEnd: Math.floor(ps.loopEnd * sr), rootHz } };
        }
        if (mode === 'live-stretch') {
          return { kind: 'stretch', layer, sample: { buffer: ps.buffer, loopStart: ps.loopStart, loopEnd: ps.loopEnd, rootHz }, formant: layer.formantLock };
        }
        const grid = selectRoots(rootMidi);
        // real recordings at other pitches (multisampled source): take the one nearest each grid point, decode them
        // in phase 1 (cheap), and let the phase vocoder fill only the gaps in phase 2
        const meta = this.samples.get(layer.sampleId);
        const noteList = meta?.notes ?? [];
        const wanted = new Map<number, number>();   // note midi → grid point it serves
        if (noteList.length) {
          for (const g of grid) {
            let bestMidi = -1, bestDist = Infinity;
            for (const n of noteList) { const d = Math.abs(n.rootMidi - g); if (d < bestDist) { bestDist = d; bestMidi = n.rootMidi; } }
            if (bestMidi >= 0 && bestDist <= 3.5 && Math.abs(bestMidi - rootMidi) > 0.5) wanted.set(bestMidi, g);
          }
        }
        const realRoots: RootBuffer[] = [];
        if (wanted.size) {
          const cap = this.tier0 ? 3 : 12;
          const midisToLoad = Array.from(wanted.keys()).sort((a, b) => Math.abs(a - rootMidi) - Math.abs(b - rootMidi)).slice(0, cap);
          let doneNotes = 0;
          this.setProgress(drone.id, 'decoding', `decoding 0 of ${midisToLoad.length} notes`, 0.15);
          await mapLimit(midisToLoad, 3, async (midi) => {
            try {
              const n = await this.samples.preparedNote(layer.sampleId, midi);
              realRoots.push({ midi: hzToMidi(n.rootHz), buffer: n.buffer, loopStart: n.loopStart, loopEnd: n.loopEnd, real: true });
            } catch (e) { console.warn(`[tonist] note ${midi} of "${layer.sampleId}" unavailable`, e); }
            doneNotes++;
            this.setProgress(drone.id, 'decoding', `decoding ${doneNotes} of ${midisToLoad.length} notes`, 0.15 + 0.35 * (doneNotes / midisToLoad.length));
          });
        }
        base.real = true;
        const roots: RootBuffer[] = [base];
        mergeRoots(roots, realRoots);
        if (this.tier0 || mode === 'resample' || layer.pitchMode === 'resample' || meta?.unpitched) return { kind: 'sample', layer, roots, single: roots.length === 1 };
        const formant = layer.formantLock || mode === 'multisample-formant';
        // grid points with no real note within 3.5 semitones get a rendered root, transposed from the nearest real one
        const nearestReal = (midi: number): RootBuffer => roots.reduce((a, b) => (Math.abs(b.midi - midi) < Math.abs(a.midi - midi) ? b : a));
        const midis = grid.filter((g) => !roots.some((r) => Math.abs(r.midi - g) <= 3.5));
        const rt = this.drones.get(drone.id);
        const gen = rt?.gen ?? -1;
        if (midis.length) upgrades.push(async () => {
          try {
            // nearest roots first so the commonly played range upgrades soonest
            midis.sort((a, b) => Math.abs(a - rootMidi) - Math.abs(b - rootMidi));
            let doneRoots = 0;
            this.setProgress(drone.id, 'rendering', `rendering 0 of ${midis.length} roots`, 0.55);
            await mapLimit(midis, 2, async (midi) => {
              // the drone may have been unloaded or re-prepared while we waited for a render slot
              if (this.drones.get(drone.id) !== rt || rt?.gen !== gen) return;
              await this.acquireRenderSlot();
              try {
                if (this.drones.get(drone.id) !== rt || rt?.gen !== gen) return;
                const from = nearestReal(midi);
                const r = await this.renderCached(layer.sampleId, ps, from, midi, formant, midiToHz(from.midi));
                mergeRoots(roots, [r]);
                doneRoots++;
                this.setProgress(drone.id, 'rendering', `rendering ${doneRoots} of ${midis.length} roots`, 0.55 + 0.45 * (doneRoots / midis.length));
              } finally { this.releaseRenderSlot(); }
            });
          } catch (e) {
            console.warn(`[tonist] multisample rendering failed for "${layer.sampleId}", staying on the real notes`, e);
          }
        });
        return { kind: 'sample', layer, roots, single: false };
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        error = error ? `${error}; ${msg}` : msg;
        console.warn(`[tonist] source unavailable for drone "${drone.id}": ${msg}`);
        return { kind: 'fallback', layer, reason: msg };
      }
    }));
    return { sources, error, upgrades };
  }

  private renderCached(sampleId: string, ps: PreparedSample, base: RootBuffer, midi: number, formantLock: boolean, rootHz: number): Promise<RootBuffer> {
    const key = `${sampleId}|${base.midi.toFixed(2)}>${midi.toFixed(3)}|${formantLock ? 1 : 0}|${ps.buffer.sampleRate}`;
    let p = this.rootCache.get(key);
    if (!p) {
      const override = (globalThis as { __tonistRenderBlockMs?: number }).__tonistRenderBlockMs;
      const started = performance.now();
      p = renderRoot({ buffer: base.buffer, loopStart: base.loopStart, loopEnd: base.loopEnd }, midi - base.midi, { formantLock, rootHz, maxLoopSec: rootLoopCap(this.tier0), blockMs: override ?? blockMsFor(midiToHz(midi)) })
        .then((r) => {
          const took = performance.now() - started;
          const seconds = r.loopEnd - r.loopStart;
          audioLog.log('load', () => `rendered root ${midi.toFixed(0)} (${seconds.toFixed(1)} s of audio) in ${took.toFixed(0)}ms`);
          // slower than real time means this device cannot keep up with the phase vocoder; later drones resample
          if (!this.slowRender && seconds > 0 && took > seconds * 1000) {
            this.slowRender = true;
            audioLog.log('load', `rendering is slower than real time here; further drones will resample instead`);
          }
          return { midi, buffer: r.buffer, loopStart: r.loopStart, loopEnd: r.loopEnd };
        })
        .catch((e: unknown) => { this.rootCache.delete(key); throw e; });
      this.rootCache.set(key, p);
    }
    return p;
  }

  private acquireRenderSlot(): Promise<void> {
    if (this.renderSlots < 2) { this.renderSlots++; return Promise.resolve(); }
    return new Promise<void>((resolve) => this.renderWaiters.push(() => { this.renderSlots++; resolve(); }));
  }
  private releaseRenderSlot(): void {
    this.renderSlots = Math.max(0, this.renderSlots - 1);
    const next = this.renderWaiters.shift();
    if (next) next();
  }

  getPitchMode(): PitchEngineMode { return this.pitchMode; }

  /** Switch the re-pitching method: re-prepare every loaded drone and swap the units of its live voices. */
  setPitchMode(mode: PitchEngineMode): void {
    if (mode === this.pitchMode) return;
    audioLog.log('load', `pitch engine ${this.pitchMode} → ${mode}`);
    this.pitchMode = mode;
    this.rootCache.clear();
    for (const rt of Array.from(this.drones.values())) {
      if (rt.status === 'loading') continue;
      rt.sourcesKey = '';
      const p = this.loadDrone(rt.drone);
      void p.then(() => {
        if (this.drones.get(rt.drone.id) !== rt) return;
        for (const v of this.voiceList) if (v.droneId === rt.drone.id && !v.done && !v.releasing) v.upgradeSources(rt.sources, true);
      }).catch(() => { /* reported in the store */ });
    }
  }

  /** Audition a sample's prepared loop through the master, bypassing every drone effect. */
  async previewSample(sampleId: string): Promise<void> {
    await this.start();
    const m = this.ensureMaster();
    const ps = await this.samples.preparedSample(sampleId);
    this.stopPreview();
    const src = m.ctx.createBufferSource();
    src.buffer = ps.buffer;
    src.loop = true; src.loopStart = ps.loopStart; src.loopEnd = ps.loopEnd;
    const gain = m.ctx.createGain();
    const t = m.ctx.currentTime;
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.9, t + 0.08);
    src.connect(gain); gain.connect(m.input);
    src.start(t, ps.loopStart);
    this.preview = { src, gain, id: sampleId };
    audioLog.log('voice', `preview ${sampleId}: loop ${ps.loopStart.toFixed(2)}–${ps.loopEnd.toFixed(2)} s at ${fmtHz(ps.rootHz)}`);
  }

  stopPreview(): void {
    const p = this.preview;
    if (!p) return;
    this.preview = null;
    const t = p.gain.context.currentTime;
    p.gain.gain.cancelScheduledValues(t);
    p.gain.gain.setValueAtTime(p.gain.gain.value, t);
    p.gain.gain.linearRampToValueAtTime(0, t + 0.15);
    try { p.src.stop(t + 0.2); } catch { /* not started */ }
    setTimeout(() => { try { p.src.disconnect(); p.gain.disconnect(); } catch { /* gone */ } }, 400);
  }

  // ---- listening harness: record the master output -------------------------------------------------------------
  private capture: { node: AudioWorkletNode; chunks: Float32Array[][]; done: Promise<void>; resolveDone: () => void; startedAt: number } | null = null;

  /** Start recording everything that reaches the output (post-limiter). Test/debug use. */
  async startCapture(): Promise<void> {
    await this.start();
    const m = this.ensureMaster();
    if (!this.cm.worklets.capture) throw new Error('capture worklet unavailable');
    if (this.capture) await this.stopCapture();
    const node = new AudioWorkletNode(m.ctx, 'tonist-capture', { numberOfInputs: 1, numberOfOutputs: 0, channelCount: 2, channelCountMode: 'explicit', channelInterpretation: 'speakers' });
    const chunks: Float32Array[][] = [];
    let resolveDone = () => { /* set below */ };
    const done = new Promise<void>((r) => { resolveDone = r; });
    node.port.onmessage = (e: MessageEvent<{ type: string; channels?: Float32Array[] }>) => {
      if (e.data.type === 'chunk' && e.data.channels) chunks.push(e.data.channels);
      else if (e.data.type === 'done') resolveDone();
    };
    m.analyser.connect(node);
    this.capture = { node, chunks, done, resolveDone, startedAt: m.ctx.currentTime };
    audioLog.log('context', 'capture started');
  }

  /** Stop recording and return the PCM (channels as Float32Array) with the sample rate. */
  async stopCapture(): Promise<{ sampleRate: number; channels: Float32Array[]; startedAt: number }> {
    const c = this.capture;
    const m = this.master;
    if (!c || !m) return { sampleRate: 0, channels: [], startedAt: 0 };
    this.capture = null;
    c.node.port.postMessage('stop');
    await Promise.race([c.done, new Promise<void>((r) => setTimeout(r, 2000))]);
    try { m.analyser.disconnect(c.node); } catch { /* gone */ }
    const total = c.chunks.reduce((a, ch) => a + (ch[0]?.length ?? 0), 0);
    const out = [new Float32Array(total), new Float32Array(total)];
    let pos = 0;
    for (const ch of c.chunks) { out[0]!.set(ch[0]!, pos); out[1]!.set(ch[1] ?? ch[0]!, pos); pos += ch[0]!.length; }
    audioLog.log('context', `capture stopped: ${(total / m.ctx.sampleRate).toFixed(2)} s`);
    return { sampleRate: m.ctx.sampleRate, channels: out, startedAt: c.startedAt };
  }

  /** Stop recording and return a 16-bit stereo WAV as base64 (for test runners pulling audio out of the page). */
  async captureWavBase64(): Promise<{ sampleRate: number; frames: number; wavBase64: string }> {
    const { sampleRate, channels } = await this.stopCapture();
    const frames = channels[0]?.length ?? 0;
    const bytes = new Uint8Array(44 + frames * 4);
    const dv = new DataView(bytes.buffer);
    const str = (o: number, s: string) => { for (let i = 0; i < s.length; i++) bytes[o + i] = s.charCodeAt(i); };
    str(0, 'RIFF'); dv.setUint32(4, 36 + frames * 4, true); str(8, 'WAVE'); str(12, 'fmt '); dv.setUint32(16, 16, true);
    dv.setUint16(20, 1, true); dv.setUint16(22, 2, true); dv.setUint32(24, sampleRate, true); dv.setUint32(28, sampleRate * 4, true);
    dv.setUint16(32, 4, true); dv.setUint16(34, 16, true); str(36, 'data'); dv.setUint32(40, frames * 4, true);
    let o = 44;
    for (let i = 0; i < frames; i++) {
      for (let c = 0; c < 2; c++) { const v = Math.max(-1, Math.min(1, channels[c]![i]!)); dv.setInt16(o, v < 0 ? v * 32768 : v * 32767, true); o += 2; }
    }
    let bin = '';
    for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 0x8000)));
    return { sampleRate, frames, wavBase64: btoa(bin) };
  }

  getVoiceSpectrum(voiceId: number, out: Uint8Array<ArrayBuffer>): boolean {
    const v = this.voiceList.find((x) => x.id === voiceId);
    return v ? v.spectrum(out) : false;
  }

  unloadDrone(droneId: string): void {
    const rt = this.drones.get(droneId);
    if (!rt) return;
    this.drones.delete(droneId);
    rt.gen++;
    const t = this.cm.ctx?.currentTime ?? 0;
    for (const v of this.voiceList) if (v.droneId === droneId) v.release(0.3);
    rt.graph.fadeOut(t, 0.4);
    const graph = rt.graph;
    setTimeout(() => graph.dispose(), 1200);
    // free cached roots for samples no remaining drone uses
    const inUse = new Set<string>();
    for (const other of this.drones.values()) for (const s of other.drone.sources) if (s.kind !== 'synth') inUse.add(s.sampleId);
    for (const s of rt.drone.sources) {
      if (s.kind === 'synth' || inUse.has(s.sampleId)) continue;
      this.samples.release(s.sampleId);
      for (const k of Array.from(this.rootCache.keys())) if (k.startsWith(`${s.sampleId}|`)) this.rootCache.delete(k);
    }
    const s = engineStore.getState();
    const loading = { ...s.loading };
    const loadErrors = { ...s.loadErrors };
    const rendering = { ...s.rendering };
    delete loading[droneId];
    delete loadErrors[droneId];
    delete rendering[droneId];
    engineStore.setState({ loading, loadErrors, rendering, loadedDrones: Array.from(this.drones.keys()) });
  }

  updateDrone(drone: Drone): void {
    const rt = this.drones.get(drone.id);
    if (!rt) { void this.loadDrone(drone).catch(() => { /* in store */ }); return; }
    const key = JSON.stringify(drone.sources);
    rt.drone = drone;
    rt.macros = macroMap(drone.macros);
    const t = this.cm.current.currentTime;
    rt.graph.apply(drone, rt.macros, t);
    for (const v of this.voiceList) if (v.droneId === drone.id) v.applyDrone(drone, rt.macros);
    if (key !== rt.sourcesKey) void this.loadDrone(drone).catch(() => { /* in store */ });
  }

  // ---- voices ---------------------------------------------------------------------------------------------------

  private createVoice(rt: DroneRuntime, hz: number, gesture: GestureState, continuous: boolean, attackScale?: number): Voice {
    const m = this.ensureMaster();
    // never a silent voice: while phase 1 is still decoding, play fallback additive units
    const sources: PreparedSource[] = rt.status === 'loading' || rt.sources.length === 0
      ? rt.drone.sources.map((layer) => ({ kind: 'fallback', layer, reason: 'loading' }))
      : rt.sources;
    const v = new Voice({
      id: this.nextVoiceId++, droneId: rt.drone.id, ctx: m.ctx, drone: rt.drone, macros: rt.macros, sources,
      destination: rt.graph.input, hz, gesture, attackScale, continuous, tier0: this.tier0, worklets: this.cm.worklets,
    });
    v.onDone = () => { this.voiceList = this.voiceList.filter((x) => x !== v); if (rt.continuous === v) rt.continuous = null; };
    this.voiceList.push(v);
    this.lastGestureAt = performance.now();
    this.startTimer();
    return v;
  }

  private enforceCap(): void {
    const active = this.voiceList.filter((v) => !v.continuous && !v.releasing && !v.done);
    while (active.length >= MAX_VOICES) {
      const victim = active.find((v) => !v.latched) ?? active[0]!;
      audioLog.log('voice', `voice cap ${MAX_VOICES}: stealing #${victim.id} (${fmtHz(victim.hz)}${victim.latched ? ', latched' : ''})`);
      victim.release(0.3);
      active.splice(active.indexOf(victim), 1);
    }
  }

  noteOn(droneId: string, hz: number, gesture: GestureState, opts?: { attackScale?: number }): VoiceHandle {
    this.cm.current; // throws with a clear message when start() has not run
    const rt = this.runtimeById(droneId);
    audioLog.log('voice', () => `noteOn ${droneId} ${fmtHz(hz)} (drone ${rt.status}, ${this.voiceList.length} voice${this.voiceList.length === 1 ? '' : 's'} sounding)`);
    this.enforceCap();
    return this.createVoice(rt, hz, gesture, false, opts?.attackScale);
  }

  setContinuous(droneId: string, hz: number, gesture: GestureState): VoiceHandle {
    this.cm.current;
    const rt = this.runtimeById(droneId);
    const v = rt.continuous;
    if (v && !v.done && !v.releasing) {
      audioLog.log('voice', () => `continuous ${droneId}: glide #${v.id} ${fmtHz(v.hz)} → ${fmtHz(hz)}`);
      v.setPitch(hz, glideSeconds(hzToMidi(hz) - hzToMidi(v.hz)));
      v.setGesture(gesture);
      this.lastGestureAt = performance.now();
      return v;
    }
    const nv = this.createVoice(rt, hz, gesture, true);
    rt.continuous = nv;
    return nv;
  }

  stopContinuous(droneId: string): void {
    const rt = this.drones.get(droneId);
    if (!rt?.continuous) return;
    audioLog.log('voice', `continuous ${droneId}: stop #${rt.continuous.id}`);
    rt.continuous.release();
    rt.continuous = null;
  }

  private tanpuraEnabled = true;
  /** The implied tanpura (quiet Sa sub-drone under everything). Off = never fades in; used by the listening harness. */
  setTanpuraEnabled(on: boolean): void {
    this.tanpuraEnabled = on;
    if (!on && this.tanpura && this.cm.ctx) this.tanpura.fadeOut(this.cm.ctx.currentTime, 0.5);
  }

  setTonicHz(hz: number): void {
    if (!(hz > 0)) return;
    this.tonicHz = hz;
    engineStore.setState({ tonicHz: hz });
    if (this.cm.ctx) this.ensureTanpura().setHz(hz / 2, this.cm.ctx.currentTime);
  }

  setMasterVolume(v: number): void {
    this.masterVolume = clamp(v, 0, 1);
    if (this.master) targetTau(this.master.gain.gain, this.masterVolume, this.master.ctx.currentTime, 0.05);
  }

  releaseAll(): void {
    for (const v of this.voiceList) v.release();
    for (const rt of this.drones.values()) rt.continuous = null;
  }

  getVoiceWaveform(voiceId: number, out: Float32Array): boolean {
    const v = this.voiceList.find((x) => x.id === voiceId);
    return v ? v.waveform(out) : false;
  }

  getMasterSpectrum(out: Uint8Array): number {
    const m = this.master;
    if (!m) { out.fill(0); return 0; }
    const target = out.length === 64 ? out : this.spectrum;
    m.analyser.getByteFrequencyData(target as Uint8Array<ArrayBuffer>);
    if (target !== out) out.set(target.subarray(0, out.length));
    const binHz = m.ctx.sampleRate / 128;
    let num = 0, den = 0;
    for (let i = 0; i < 64; i++) { const mag = target[i]!; num += i * binHz * mag; den += mag; }
    return den > 0 ? num / den : 0;
  }

  voices(): readonly VoiceHandle[] { return this.voiceList; }

  // ---- tick -----------------------------------------------------------------------------------------------------

  private startTimer(): void {
    if (this.timer) return;
    this.timer = setInterval(() => this.tick(), TICK_MS);
  }

  private tick(): void {
    const ctx = this.cm.ctx;
    if (!ctx) return;
    const now = ctx.currentTime;
    const dt = this.lastTick > 0 ? clamp(now - this.lastTick, 0, 0.25) : TICK_MS / 1000;
    this.lastTick = now;
    if (ctx.state !== 'running') return;
    ticker.run(now, dt);
    this.voiceList = this.voiceList.filter((v) => !v.done);
    const idle = performance.now() - this.lastGestureAt > IDLE_MS;
    const sounding: Voice[] = [];
    for (const v of this.voiceList) { v.setIdle(idle); if (!v.releasing) sounding.push(v); }
    // implied tanpura: in while anything sounds and no latched/continuous voice is already holding Sa
    const tanpura = this.tanpura;
    if (tanpura) {
      const saHeld = this.tonicHz > 0 && sounding.some((v) => (v.latched || v.continuous) && pitchClassDistance(this.tonicHz, v.hz) < 0.5);
      if (this.tanpuraEnabled && this.tonicHz > 0 && sounding.length > 0 && !saHeld) tanpura.fadeIn(now); else tanpura.fadeOut(now, 6);
    }
    // watchdog: a non-finite sample anywhere upstream poisons the compressor for good (silence until reload).
    // Detect it at the master and rebuild the graph instead.
    const peak = this.masterPeak();
    if (!Number.isFinite(peak)) { this.recoverGraph('non-finite samples reached the master output'); return; }
    this.healthTick(ctx, now, peak);
    if (this.voiceList.length > 0 || this.lastVoiceCount !== 0 || peak > 0.001 || this.lastPeak > 0.001) {
      const voices: EngineVoiceMeter[] = this.voiceList.map((v) => ({
        id: v.id, droneId: v.droneId, hz: v.hz, amplitude: v.amplitude(), latched: v.latched, releasing: v.releasing, continuous: v.continuous, kinds: v.sourceKinds,
      }));
      engineStore.setState({ voices, tanpuraOn: tanpura?.active ?? false, masterPeak: peak, pitchMode: this.pitchMode });
      this.lastVoiceCount = this.voiceList.length;
      this.lastPeak = peak;
    }
  }

  private readonly waveScratch = new Float32Array(128);
  private lastPeak = 0;

  private readonly health = { wall: 0, audio: 0, lag: 0, peakMax: 0, reportAt: 0, ticks: 0 };
  /**
   * Debug health line every ~5 s. "audio clock lag" is how far ctx.currentTime fell behind the wall clock: the
   * rendering thread could not produce audio as fast as the device consumed it, so that much time was played as
   * glitches (Chrome does not expose underruns otherwise). Ticks counts main-thread timer ticks (≈150 per 5 s at
   * 33 ms; far fewer means the main thread was starved or the tab throttled).
   */
  private healthTick(ctx: AudioContext, now: number, peak: number): void {
    const h = this.health;
    if (!audioDebugEnabled()) { h.wall = 0; h.reportAt = 0; return; }
    const wall = performance.now() / 1000;
    if (h.wall > 0) h.lag += (wall - h.wall) - (now - h.audio);
    h.wall = wall; h.audio = now; h.ticks++;
    if (peak > h.peakMax) h.peakMax = peak;
    if (h.reportAt === 0) { h.reportAt = wall; return; }
    const span = wall - h.reportAt;
    if (span < 5) return;
    const lagMs = h.lag * 1000;
    const sounding = this.voiceList.filter((v) => !v.releasing).length;
    const rendering = Object.keys(engineStore.getState().rendering).length;
    const out = 'outputLatency' in ctx ? (ctx as AudioContext & { outputLatency: number }).outputLatency : 0;
    audioLog.log('context', `health: audio clock lag ${lagMs.toFixed(0)} ms over ${span.toFixed(1)} s${lagMs > 50 ? ' — the audio thread fell behind, that much was heard as glitches' : ''}; latency base ${(ctx.baseLatency * 1000).toFixed(1)} ms, output ${(out * 1000).toFixed(1)} ms; state ${ctx.state}; voices ${this.voiceList.length} (${sounding} sounding); rendering ${rendering}; master peak ${h.peakMax > 0 ? (20 * Math.log10(h.peakMax)).toFixed(1) + ' dBFS' : 'silent'}; ticks ${h.ticks}; hidden ${document.hidden}`);
    h.lag = 0; h.peakMax = 0; h.reportAt = wall; h.ticks = 0;
  }
  private lastRecovery = 0;

  /** Absolute peak of the last render quantum at the master analyser; NaN if the graph is poisoned. */
  private masterPeak(): number {
    const m = this.master;
    if (!m) return 0;
    m.analyser.getFloatTimeDomainData(this.waveScratch);
    let p = 0;
    for (let i = 0; i < this.waveScratch.length; i++) { const v = this.waveScratch[i]!; if (!Number.isFinite(v)) return NaN; const a = Math.abs(v); if (a > p) p = a; }
    return p;
  }

  /** Copy the master waveform (up to 128 samples) into `out`; returns the peak. */
  getMasterWaveform(out: Float32Array): number {
    const m = this.master;
    if (!m) { out.fill(0); return 0; }
    m.analyser.getFloatTimeDomainData(this.waveScratch);
    const n = Math.min(out.length, this.waveScratch.length);
    let p = 0;
    for (let i = 0; i < n; i++) { const v = this.waveScratch[i]!; out[i] = v; const a = Math.abs(v); if (a > p) p = a; }
    return p;
  }

  /** Tear down every voice, the master chain and the drone FX graphs and rebuild them on the same context. */
  recoverGraph(reason: string): void {
    const ctx = this.cm.ctx;
    if (!ctx) return;
    const now = performance.now();
    if (now - this.lastRecovery < 2000) return;
    this.lastRecovery = now;
    audioLog.error('watchdog', `${reason} — rebuilding the audio graph (voices, FX buses, master)`);
    for (const v of this.voiceList) v.dispose();
    this.voiceList = [];
    const old = this.master;
    this.master = null;
    if (old) for (const n of [old.input, old.comp, old.limiter, old.gain, old.analyser]) { try { n.disconnect(); } catch { /* gone */ } }
    this.tanpura?.dispose();
    this.tanpura = null;
    const m = this.ensureMaster();
    this.ensureTanpura();
    for (const rt of this.drones.values()) {
      rt.graph.dispose();
      rt.graph = new DroneGraph(ctx, rt.drone, rt.macros, this.cm.worklets);
      rt.graph.output.connect(m.input);
      rt.continuous = null;
    }
    const s = engineStore.getState();
    engineStore.setState({ voices: [], masterPeak: 0, recoveries: s.recoveries + 1 });
  }
}
